import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useFooterSave } from '../../components/settings/footerSave';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { FormField, Input } from '../../components/ui/Field';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { KeyStatus, MASKED, SetupLink, SetupSteps } from './integrationUi';
import { usePlatformSettingsSave, SaveRow } from './settingsSave';

const FIGMA_PAT_HINT =
  'Kept on our server (write-only — never shown again) and used to read this install’s Figma files.';

/**
 * Install-level Figma integration. Figma has no OAuth app to register for this use case — it
 * authenticates with a PERSONAL ACCESS TOKEN — so this card is shaped exactly like the Apify
 * card: a write-only token plus a live identity lookup, which is the only way to tell WHICH
 * Figma account a masked token belongs to.
 *
 * On Site Integrations the page's sticky Save footer saves it (useFooterSave), and its own Save
 * button is left out; rendered anywhere without a footer it keeps that button.
 */
export function FigmaCard({ patSet }: { patSet: boolean }) {
  // The PAT is write-only. When one is stored the field shows the MASKED mask; focusing it
  // clears the mask so a new token can be typed. `patTouched` distinguishes "the admin is
  // entering a new token" from "leave the stored token alone".
  const [pat, setPat] = useState('');
  const [patTouched, setPatTouched] = useState(false);
  const { saving, saved, setSaved, error, save } = usePlatformSettingsSave();
  const toast = useToast();

  // Live lookup of the account behind the configured token. Invalidated on save (see settingsSave).
  const identityQuery = useQuery({
    queryKey: ['figma-identity'],
    queryFn: ({ signal }) => api.getFigmaIdentity(signal),
  });
  const identity = identityQuery.data;

  const patDisplay = patTouched ? pat : patSet ? MASKED : '';
  const dirty = patTouched && pat.trim() !== '';

  // Re-mask the token field once a save lands (the "Key set" badge reflects the result).
  useEffect(() => {
    if (saved) {
      setPat('');
      setPatTouched(false);
    }
  }, [saved]);

  // Sent only while a new token is typed (`dirty`): the stored one is never re-sent or cleared.
  const { inFooter } = useFooterSave({
    id: 'figma',
    dirty,
    saving,
    save: () => save({ figmaPat: pat.trim() }),
    // Back to the masked, saved state.
    discard: () => {
      setPat('');
      setPatTouched(false);
    },
  });

  // The card's own Save, drawn only where no footer saves it.
  const onSave = () =>
    void save({ figmaPat: pat.trim() }).then((ok) => {
      if (ok) toast.show(CHANGES_SAVED);
    });

  return (
    <Card>
      <CardHeader
        title="Figma Integration"
        description="Personal access token used to read this install's Figma files and designs."
      />
      <CardBody className="space-y-4">
        <SetupSteps
          title="Set up Figma"
          action={
            <SetupLink href="https://www.figma.com/developers/api#access-tokens">
              Where do I get this?
            </SetupLink>
          }
          steps={[
            <>
              Open Figma → <span className="font-semibold">Settings</span> →{' '}
              <span className="font-semibold">Security</span> tab →{' '}
              <span className="font-semibold">Personal access tokens</span>.
            </>,
            <>
              Generate a new token and grant two read-only scopes:{' '}
              <span className="font-semibold">File content: Read-only</span> and{' '}
              <span className="font-semibold">Current user: Read-only</span>. The second is what
              lets the check below confirm which account the token belongs to.
            </>,
            <>
              Paste the token below and <span className="font-semibold">Save</span> — it is stored
              write-only and never shown again.
            </>,
          ]}
        />

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink-strong">Status</span>
          <KeyStatus set={patSet} />
        </div>

        {/* Live-verified account behind the configured token — the reliable way to tell which
            Figma account is active, since the masked token can't reveal it. */}
        <div className="rounded-lg border border-line bg-page/60 px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium text-ink-label">Configured Figma account</span>
            <button
              type="button"
              onClick={() => void identityQuery.refetch()}
              disabled={identityQuery.isFetching}
              className="focus-ring text-xs font-medium text-link hover:text-action-hover hover:underline disabled:opacity-50"
            >
              {identityQuery.isFetching ? 'Checking…' : 'Re-check'}
            </button>
          </div>
          <div className="mt-1 text-sm">
            {identityQuery.isLoading ? (
              <span className="text-ink-faint">Checking…</span>
            ) : identityQuery.isError ? (
              <span className="text-red-600 dark:text-error-dark">Could not check the configured account.</span>
            ) : !identity?.configured ? (
              <span className="text-ink-faint">No Figma token configured.</span>
            ) : identity.error ? (
              <span className="text-amber-700 dark:text-warning-dark">
                Token set, but couldn’t verify: {identity.error}
              </span>
            ) : (
              <span className="text-ink">
                <span className="font-medium">{identity.handle ?? 'Figma account'}</span>
                {identity.email && <span className="text-ink-muted"> · {identity.email}</span>}
              </span>
            )}
          </div>
        </div>

        <FormField
          label={patSet ? 'Replace Figma personal access token' : 'Figma personal access token'}
          htmlFor="figma-pat"
          hint={FIGMA_PAT_HINT}
        >
          <Input
            id="figma-pat"
            type="password"
            autoComplete="off"
            value={patDisplay}
            onFocus={() => {
              // First focus on a masked field clears the mask so the admin types a fresh token.
              if (!patTouched) {
                setPatTouched(true);
                setPat('');
              }
            }}
            onChange={(e) => {
              setSaved(false);
              setPatTouched(true);
              setPat(e.target.value);
            }}
            onBlur={() => {
              // Left untouched-empty → restore the mask (means "keep the stored token").
              if (patTouched && pat === '') setPatTouched(false);
            }}
            placeholder={patSet ? MASKED : 'figd_…'}
          />
        </FormField>

        <SaveRow inFooter={inFooter} saving={saving} saved={saved} dirty={dirty} error={error} onSave={onSave} />
      </CardBody>
    </Card>
  );
}
